/**
 * Client Dashboard Component
 *
 * Displays comprehensive financial performance metrics for a specific client.
 * Shows actual revenue vs PROA budget with variance analysis.
 *
 * Features:
 * - Year selector for budget period navigation
 * - Four performance summary cards (Realizado, PROA, Performance %, Variance)
 * - Historical revenue vs PROA target chart (3-year trends)
 * - Budget settings form for PROA targets, costs, and labour allocation
 * - Real-time data fetching from budget and SIG APIs
 * - Color-coded variance indicators (green = positive, red = negative)
 * - Loading skeletons and error handling
 * - Synchronized state across all dashboard sections
 * - Post-save refresh for metrics and charts
 *
 * Epic: MONU-FINANCIAL-SETTING-epic-003
 * Story: MONU-FINANCIAL-SETTING-story-3.005
 *
 * @see docs/stories/MONU-FINANCIAL-SETTING-story-3.005.md
 * @module app/clients/client-dashboard
 */

import React, { useState, useMemo } from 'react'
import { useParams } from '@tanstack/react-router'
import { useClientBudget } from '@/hooks/useClientBudget'
import { getClients } from '@/api/sig-data'
import { formatCurrency, formatPercent, calculateVariance } from '@/types/budgets'
import type { ClientResultApiResponse } from '@/types/financial'
import type { BudgetFormData } from '@/types/budgets'
import { HistoricalChart } from '@/components/charts/HistoricalChart'
import { BudgetSettingsForm } from '@/components/budgets/BudgetSettingsForm'

/**
 * Performance Card Component
 * Reusable card for displaying a single performance metric
 */
interface PerformanceCardProps {
  title: string
  value: string | number
  subtitle?: string
  variant?: 'default' | 'positive' | 'negative'
  loading?: boolean
}

function PerformanceCard({ title, value, subtitle, variant = 'default', loading }: PerformanceCardProps) {
  if (loading) {
    return (
      <div className="card rounded-monumenta p-6 animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
        <div className="h-8 bg-gray-200 rounded w-3/4 mb-2"></div>
        {subtitle && <div className="h-3 bg-gray-200 rounded w-1/3"></div>}
      </div>
    )
  }

  const variantClasses = {
    default: 'border-brand-dark',
    positive: 'border-green-500 text-green-700',
    negative: 'border-red-500 text-red-700',
  }

  return (
    <div className={`card rounded-monumenta p-6 border-l-4 ${variantClasses[variant]}`}>
      <h3 className="text-sm font-medium text-gray-600 mb-2">{title}</h3>
      <p className={`text-3xl font-bold ${variant !== 'default' ? '' : 'text-brand-dark'}`}>
        {value}
      </p>
      {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
    </div>
  )
}

/**
 * Year Selector Component
 * Tabs for switching between available budget years
 */
interface YearSelectorProps {
  selectedYear: number
  onYearChange: (year: number) => void
  availableYears?: number[]
}

function YearSelector({ selectedYear, onYearChange, availableYears = [2024, 2025] }: YearSelectorProps) {
  return (
    <div className="flex gap-2">
      {availableYears.map((year) => (
        <button
          key={year}
          onClick={() => onYearChange(year)}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            selectedYear === year
              ? 'bg-brand-primary text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {year}
        </button>
      ))}
    </div>
  )
}

/**
 * Error Display Component
 * User-friendly error message with retry button
 */
interface ErrorDisplayProps {
  message: string
  onRetry: () => void
}

function ErrorDisplay({ message, onRetry }: ErrorDisplayProps) {
  return (
    <div className="card rounded-monumenta p-8 text-center">
      <div className="mb-4">
        <svg
          className="mx-auto h-12 w-12 text-red-500"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">Erro ao carregar dados</h3>
      <p className="text-gray-600 mb-6">{message}</p>
      <button
        onClick={onRetry}
        className="px-6 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-dark transition-colors"
      >
        Tentar Novamente
      </button>
    </div>
  )
}

/**
 * Main Client Dashboard Component
 */
export default function ClientDashboard() {
  const { clientCode } = useParams({ from: '/_auth/clients/$clientCode/dashboard' })
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear())

  // Fetch budget data
  const { data: budgetData, loading: budgetLoading, error: budgetError, refetch: refetchBudget } = useClientBudget(clientCode, selectedYear)

  // Fetch actual performance data (SIG data)
  const [sigData, setSigData] = React.useState<ClientResultApiResponse | null>(null)
  const [sigLoading, setSigLoading] = React.useState<boolean>(true)
  const [sigError, setSigError] = React.useState<Error | null>(null)

  // Fetch SIG data when year changes
  React.useEffect(() => {
    let isMounted = true

    const fetchSigData = async () => {
      try {
        setSigLoading(true)
        setSigError(null)

        const clients = await getClients(selectedYear.toString())
        const client = clients.find(c => c.clientCode === clientCode)

        if (isMounted) {
          setSigData(client || null)
          setSigLoading(false)
        }
      } catch (err) {
        if (isMounted) {
          setSigError(err instanceof Error ? err : new Error('Failed to fetch client data'))
          setSigLoading(false)
        }
      }
    }

    fetchSigData()

    return () => {
      isMounted = false
    }
  }, [clientCode, selectedYear])

  // Calculate performance metrics
  const performanceMetrics = useMemo(() => {
    if (!budgetData?.settings || !sigData) {
      return null
    }

    const actual = sigData.revenue || 0
    const target = budgetData.settings.targetRevenue || 0
    const variance = calculateVariance(actual, target)
    const performancePercent = target > 0 ? (actual / target) * 100 : 0

    return {
      actual,
      target,
      variance,
      performancePercent,
      variancePercent: target > 0 ? (variance / target) * 100 : 0
    }
  }, [budgetData, sigData])

  // Handle retry for errors
  const handleRetry = () => {
    refetchBudget()
    // Re-trigger SIG data fetch by updating state
    setSelectedYear(prev => prev) // Force re-render
  }

  // Handle budget save - refresh all dependent data
  const handleBudgetSave = async (values: BudgetFormData) => {
    // Parent onSave callback passed to BudgetSettingsForm handles the API call
    // After successful save, we need to refetch data to update metrics and chart

    // Refetch budget data to get updated metadata
    await refetchBudget()

    // Refetch SIG data to recalculate performance metrics with new PROA target
    // This is done by re-triggering the effect
    setSelectedYear(prev => prev)
  }

  // Convert BudgetSettings to BudgetFormData (normalize undefined to null)
  const budgetFormData: BudgetFormData | null = useMemo(() => {
    if (!budgetData?.settings) return null

    return {
      targetRevenue: budgetData.settings.targetRevenue ?? null,
      targetCosts: budgetData.settings.targetCosts ?? null,
      targetTax: budgetData.settings.targetTax ?? null,
      targetMargin: budgetData.settings.targetMargin ?? null,
      labourCost: budgetData.settings.labourCost ?? null,
      approvedWork: budgetData.settings.approvedWork ?? null,
    }
  }, [budgetData])

  // Determine loading state
  const loading = budgetLoading || sigLoading
  const error = budgetError || sigError
  const hasData = budgetData && sigData && performanceMetrics

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6 md:space-y-8">
      {/* Header Section */}
      <div>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-display font-bold text-brand-dark">
              {budgetData?.clientName || clientCode}
            </h1>
            <p className="text-gray-600 mt-1">Dashboard de Performance Financeira</p>
          </div>
          <YearSelector
            selectedYear={selectedYear}
            onYearChange={setSelectedYear}
          />
        </div>
      </div>

      {/* Error State */}
      {error && !loading && (
        <ErrorDisplay
          message={error.message || 'Não foi possível carregar os dados do cliente'}
          onRetry={handleRetry}
        />
      )}

      {/* Performance Cards Grid */}
      {!error && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          {/* Realizado Card */}
          <PerformanceCard
            title="Realizado"
            value={hasData ? formatCurrency(performanceMetrics.actual) : '—'}
            subtitle="Receita efetiva no período"
            loading={loading}
          />

          {/* PROA Target Card */}
          <PerformanceCard
            title="PROA (Meta)"
            value={hasData ? formatCurrency(performanceMetrics.target) : '—'}
            subtitle="Receita projetada"
            loading={loading}
          />

          {/* Performance % Card */}
          <PerformanceCard
            title="Performance"
            value={hasData ? formatPercent(performanceMetrics.performancePercent, 1) : '—'}
            subtitle="Realizado / PROA"
            variant={
              hasData
                ? performanceMetrics.performancePercent >= 100
                  ? 'positive'
                  : performanceMetrics.performancePercent >= 80
                    ? 'default'
                    : 'negative'
                : 'default'
            }
            loading={loading}
          />

          {/* Variance Card */}
          <PerformanceCard
            title="Variação"
            value={hasData ? formatCurrency(performanceMetrics.variance) : '—'}
            subtitle={
              hasData
                ? `${performanceMetrics.variancePercent >= 0 ? '+' : ''}${formatPercent(performanceMetrics.variancePercent, 1)} vs PROA`
                : undefined
            }
            variant={
              hasData
                ? performanceMetrics.variance >= 0
                  ? 'positive'
                  : 'negative'
                : 'default'
            }
            loading={loading}
          />
        </div>
      )}

      {/* No Data State */}
      {!loading && !error && !hasData && (
        <div className="card rounded-monumenta p-8 text-center">
          <p className="text-gray-600">
            Nenhum dado disponível para o cliente <strong>{clientCode}</strong> no ano <strong>{selectedYear}</strong>
          </p>
        </div>
      )}

      {/* Historical Chart Section */}
      {!error && hasData && (
        <div className="card rounded-monumenta p-4 md:p-6">
          <h2 className="text-xl md:text-2xl font-display font-bold text-brand-dark mb-4 md:mb-6">
            Histórico de Performance
          </h2>
          <HistoricalChart
            clientCode={clientCode}
            selectedYear={selectedYear}
            className="w-full"
          />
        </div>
      )}

      {/* Budget Settings Form Section */}
      {!error && budgetFormData && (
        <div className="card rounded-monumenta p-4 md:p-6">
          <h2 className="text-xl md:text-2xl font-display font-bold text-brand-dark mb-4 md:mb-6">
            Configurações de Orçamento
          </h2>
          <BudgetSettingsForm
            clientCode={clientCode}
            year={selectedYear}
            initialValues={budgetFormData}
            metadata={budgetData?.metadata}
            onSave={handleBudgetSave}
          />
        </div>
      )}
    </div>
  )
}
